import { useAuth } from "@elit-hair/elit-tools";
import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { agentSeatAssignmentKeys } from "@/hooks/queries/use-agent-seat-assignment.queries";
import { useInactivityDetector } from "@/hooks/use-inactivity-detector";
import { SOCKET_NAMESPACES } from "@/pages/calls/utils/transcript-socket";
import { UserStatus } from "@/types/user-statuses.types";

import { useSocket } from "../hooks/useSocket";

export enum UserStatusEvents {
    statusUpdateRequest = "status:update:request",
    statusUpdateResponse = "status:update:response",
    getAllStatusesRequest = "status:getAll:request",
    getAllStatusesResponse = "status:getAll:response",
    seatAssignmentUpdated = "seat-assignment:updated",
    seatAssignmentDeleted = "seat-assignment:deleted",
}

export type SocketEvents = {
    [UserStatusEvents.statusUpdateResponse]: Record<string, UserStatus>;
    [UserStatusEvents.getAllStatusesResponse]: Record<string, UserStatus>;
    [UserStatusEvents.getAllStatusesRequest]: void;

    [UserStatusEvents.statusUpdateRequest]: UserStatus | null;

    [UserStatusEvents.seatAssignmentUpdated]: void;
    [UserStatusEvents.seatAssignmentDeleted]: void;
};

type UserStatusContextType = {
    // User status
    status: UserStatus | null;
    isLoading: boolean;
    userStatuses: Record<string, UserStatus>;

    // Socket
    isConnected: boolean;
    updateStatus: (status: UserStatus) => void;
};

const userStatusContext = createContext<UserStatusContextType | undefined>(undefined);

export const UserStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userData } = useAuth();

    const { isConnected, emit, useSocketCallback, connect } = useSocket<SocketEvents>({
        namespace: SOCKET_NAMESPACES.USER_STATUS,
        autoConnect: false,
    });

    const [status, setStatus] = useState<UserStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const previousStatusRef = useRef<UserStatus | null>(null);

    const [userStatuses, setUserStatuses] = useState<Record<string, UserStatus>>({});

    // Ensure listeners are registered first (above), then connect whenever disconnected.
    useEffect(() => {
        if (!isConnected) {
            connect();
            return;
        }
    }, [isConnected, connect]);

    // Get all statuses
    // TODO: This should be in one provider only for the user's management page, to avoid processing the statuses in the main provider
    // This affect the performance of the main provider
    useSocketCallback(UserStatusEvents.getAllStatusesResponse, (statuses: Record<string, UserStatus>) => {
        const convertedStatuses: Record<string, UserStatus> = {};
        Object.entries(statuses).forEach(([userId, status]) => {
            if (Object.values(UserStatus).includes(status)) {
                convertedStatuses[userId] = status;
            }
        });
        setUserStatuses(convertedStatuses);
    });

    // Get logged in user's status
    useSocketCallback(UserStatusEvents.statusUpdateResponse, (response: Record<string, UserStatus>) => {
        const newStatus = response?.status || response;
        if (newStatus && Object.values(UserStatus).includes(newStatus)) {
            setStatus(newStatus);

            // Update user statuses to mark current user as active
            if (userData?.id) {
                setUserStatuses((prev) => ({ ...prev, [userData.id]: newStatus }));
            }
        }
    });

    const queryClient = useQueryClient();
    const handleSeatAssignmentDeleted = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: agentSeatAssignmentKeys.list() });
    }, [queryClient]);
    const handleSeatAssignmentUpdated = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: agentSeatAssignmentKeys.list() });
    }, [queryClient]);

    useSocketCallback(UserStatusEvents.seatAssignmentDeleted, handleSeatAssignmentDeleted);
    useSocketCallback(UserStatusEvents.seatAssignmentUpdated, handleSeatAssignmentUpdated);

    // Method to update the user's status
    const updateStatus = useCallback(
        (status: UserStatus) => {
            // All statuses are handled by the backend
            emit(UserStatusEvents.statusUpdateRequest, status);
            setIsLoading(true);
        },
        [emit]
    );

    // Handle inactivity detection
    const handleInactive = () => {
        if (
            status &&
            status !== UserStatus.OFFLINE &&
            status !== UserStatus.INACTIVE &&
            status !== UserStatus.IN_CALL
        ) {
            previousStatusRef.current = status;
            updateStatus(UserStatus.INACTIVE);
        }
    };

    const handleActive = () => {
        if (
            previousStatusRef.current &&
            previousStatusRef.current !== UserStatus.OFFLINE &&
            previousStatusRef.current !== UserStatus.INACTIVE &&
            previousStatusRef.current !== UserStatus.IN_CALL
        ) {
            const statusToRestore = previousStatusRef.current;
            previousStatusRef.current = null; // Clear it before the update
            updateStatus(statusToRestore);
        }
    };

    // Initialize inactivity detector
    useInactivityDetector({
        onInactive: handleInactive,
        onActive: handleActive,
        currentStatus: status,
        isEnabled: isConnected && status !== null && status !== UserStatus.OFFLINE,
    });

    // Automatically set status to OFFLINE when socket disconnects
    useEffect(() => {
        if (!isConnected && status && status !== UserStatus.OFFLINE) {
            console.log("Socket disconnected, setting status to OFFLINE");
            setStatus(UserStatus.OFFLINE);
        }
    }, [isConnected, status]);

    // Set loading to false once we have a status
    useEffect(() => {
        if (status !== null) {
            setIsLoading(false);
        }
    }, [status]);

    return (
        <userStatusContext.Provider
            value={{
                status,
                isLoading,
                userStatuses,
                isConnected,
                updateStatus,
            }}
        >
            {children}
        </userStatusContext.Provider>
    );
};

export const useUserStatusContext = () => {
    const ctx = useContext(userStatusContext);
    if (!ctx) throw new Error("useUserContext must be used within a UserProvider");
    return ctx;
};
